import { IS_SIDEBAR_PANEL_ENABLED } from '@/components/constants/flags';
import { useIsScheduleUploadPdfEnabled } from '@/features/flags';
import { useSidebarUploadArea } from '@/features/upcomingSessionTab/hooks/useSidebarUploadArea';
import { useScheduleUpload } from '@/features/uploadSessionSchedule/hooks/useScheduleUpload';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { FileDropzone } from '@heidi/design-system/file-upload';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyFootNote,
} from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { ArrowUpToLine } from 'lucide-react';
import Link from 'next/link';

// Paste is owned by `useSidebarSchedulePaste` in the tab, not by this dropzone:
// the overlay only reveals over an empty list, so a dropzone-scoped listener
// would go missing as soon as the user has upcoming sessions.
export default function SidebarSessionUploadAreaOverlay() {
  const { t } = useTranslations();
  const {
    acceptFormats,
    maxSize,
    isUploading,
    handleFilesAccepted,
    handleFilesRejected,
  } = useScheduleUpload({ entryPoint: 'sidebar' });
  const { showSidebarUploadArea } = useSidebarUploadArea();
  const isPdfEnabled = useIsScheduleUploadPdfEnabled();

  return (
    <div
      // Opacity/pointer-events hiding is invisible to visibility checks;
      // data-state is the observable reveal signal.
      data-testid="sidebar-schedule-upload-overlay"
      data-state={showSidebarUploadArea ? 'revealed' : 'hidden'}
      className={cn(
        'absolute inset-0 z-50',
        IS_SIDEBAR_PANEL_ENABLED() ? 'bg-surface-accent' : 'bg-surface',
        showSidebarUploadArea
          ? 'pointer-events-auto opacity-100'
          : 'pointer-events-none opacity-0'
      )}
    >
      <FileDropzone
        data-testid="sidebar-schedule-upload-dropzone"
        accept={acceptFormats}
        maxSize={maxSize}
        maxFiles={1}
        disabled={isUploading}
        onFilesAccepted={handleFilesAccepted}
        onFilesRejected={handleFilesRejected}
        className="h-full"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="flex flex-col items-center gap-1">
            <ArrowUpToLine size={20} className="text-text-secondary" />
            <TypographyCaptionBold className="w-full text-text-primary">
              {t('sessions.upcomingSessions.sidebarUploadTitle')}
            </TypographyCaptionBold>
            <TypographyCaption className="text-text-secondary">
              {t(
                isPdfEnabled
                  ? 'sessions.upcomingSessions.sidebarUploadDescriptionWithPdf'
                  : 'sessions.upcomingSessions.sidebarUploadDescription'
              )}
            </TypographyCaption>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Button disabled={isUploading} size="xs">
              {t('sessions.upcomingSessions.browseFiles')}
            </Button>
            <TypographyCaption className="text-text-secondary">
              {t('sessions.upcomingSessions.orDragFilesHere')}
            </TypographyCaption>
          </div>
          <div className="flex flex-col gap-1.5 rounded-md bg-surface-accent p-3">
            <TypographyFootNote className="text-text-secondary">
              {t('sessions.upcomingSessions.uploadDescriptionShort')}
            </TypographyFootNote>
            <Button
              asChild
              variant="secondary"
              size="xxs"
              className="hover:text-text-primary"
            >
              <Link
                href="/samples/schedule-example.csv"
                download
                onClick={(e) => e.stopPropagation()}
              >
                {t('sessions.upcomingSessions.downloadCsvTemplate')}
              </Link>
            </Button>
          </div>
        </div>
      </FileDropzone>
    </div>
  );
}
